'use client';

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import * as React from 'react';

import { cn } from 'utils/cn';
import { useOverlayScope } from 'components/ui/overlay-motion';

// Local component, not the registry's tooltip: that one is a compound
// `Tooltip`/`TooltipTrigger`/`TooltipContent` with a dark bubble and the default collision boundary,
// neither of which the call sites here can use. `shadcn add tooltip --overwrite` replaces this file.
//
// Base-ui tooltip in the app's look: a white bubble with dark-grey text and a soft shadow, above
// the trigger after a short delay, 80ms where the new motion applies (the new design and the AI
// surfaces) and 100ms in classic. The delay sits on the trigger: the one `TooltipPrimitive.Provider`
// is at the app root (`App.tsx`), so once a tooltip shows, the next one opens at once. `content` is
// the bubble; the single child element becomes the trigger — its props are merged via `render`, so no
// extra wrapper node is inserted.
//
// `collisionBoundary` is set to <body>: the default ('clipping-ancestors') confines the popup to the
// nearest clipping ancestor, and the managed DataTable cells have `overflow: hidden` — that would trap
// the tooltip inside its row so it can't extend over the row separators. Bounding to <body> lets it
// float over the rows (the portal already lifts it above everything).
function Tooltip({
    content,
    children,
    delay,
    side = 'top',
    sideOffset = 6,
    className,
    open,
    onOpenChange,
    trackCursorAxis,
    disableHoverablePopup,
    closeOnClick,
    disabled,
}: {
    content: React.ReactNode;
    children: React.ReactElement;
    delay?: number;
    side?: 'top' | 'bottom' | 'left' | 'right';
    sideOffset?: number;
    className?: string;
    /** Keep the bubble up through a click, for a press answered in place, such as a copy button's check. */
    closeOnClick?: boolean;
} & Pick<
    TooltipPrimitive.Root.Props,
    'open' | 'onOpenChange' | 'trackCursorAxis' | 'disableHoverablePopup' | 'disabled'
>) {
    const { container, ai } = useOverlayScope();
    // 80ms is `--duration-micro`, the tooltip's delay on the motion scale.
    const newMotion = ai || (typeof document !== 'undefined' && document.documentElement.dataset.design === 'new');
    return (
        <TooltipPrimitive.Root
            open={open}
            onOpenChange={onOpenChange}
            trackCursorAxis={trackCursorAxis}
            disableHoverablePopup={disableHoverablePopup}
            disabled={disabled}
        >
            <TooltipPrimitive.Trigger
                render={children}
                delay={delay ?? (newMotion ? 80 : 100)}
                closeOnClick={closeOnClick}
            />
            <TooltipPrimitive.Portal container={container}>
                <TooltipPrimitive.Positioner
                    side={side}
                    sideOffset={sideOffset}
                    collisionBoundary={typeof document !== 'undefined' ? document.body : undefined}
                    className="z-(--z-tooltip)"
                >
                    <TooltipPrimitive.Popup
                        className={cn(
                            'bg-grey-0 text-grey-700 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 new:outline-hidden shadow-tooltip max-w-[80ch] origin-(--transform-origin) rounded-[4px] px-2 py-1 text-xs leading-5 whitespace-pre-line duration-100 motion-reduce:animate-none!',
                            className
                        )}
                    >
                        {content}
                    </TooltipPrimitive.Popup>
                </TooltipPrimitive.Positioner>
            </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
    );
}

export { Tooltip };
